import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import Login from "./pages/Login";
import PasswordReset from "./pages/PasswordReset";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPasswordWithToken from "./pages/ResetPasswordWithToken";
import AdminDashboard from "./pages/AdminDashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import EmployeeManagement from "./pages/EmployeeManagement";
import CredentialsList from "./pages/CredentialsList";
import StatsEntry from "./pages/StatsEntry";
import PartnerBillings from "./pages/PartnerBillings";
import BenchmarkSettings from "./pages/BenchmarkSettings";
import Documents from "./pages/Documents";
import Feedback from "./pages/Feedback";
import FirmTemplates from "./pages/FirmTemplates";
import Financials from "./pages/Financials";
import SupervisorDashboard from "./pages/SupervisorDashboard";
import TaxCalculator from "./pages/TaxCalculator";
import Training from "./pages/Training";
import Amortization from "./pages/Amortization";
import PasswordManager from "./pages/PasswordManager";
import ContactDirectory from "./pages/ContactDirectory";
import Handbook from "./pages/Handbook";
import W9Requests from "./pages/W9Requests";
import W9PublicForm from "./pages/W9PublicForm";
import DigitalShelf from "./pages/DigitalShelf";
import ShelfBoard from "./pages/ShelfBoard";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import "./App.css";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isImpersonating } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user needs to reset password (skip if impersonating)
  if (user.must_reset_password && !isImpersonating && window.location.pathname !== "/reset-password") {
    return <Navigate to="/reset-password" replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password-token" element={<ResetPasswordWithToken />} />
      <Route
        path="/reset-password"
        element={
          <ProtectedRoute>
            <PasswordReset />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <EmployeeDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="employees"
          element={
            <ProtectedRoute adminOnly>
              <EmployeeManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="credentials"
          element={
            <ProtectedRoute adminOnly>
              <CredentialsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="stats"
          element={
            <ProtectedRoute adminOnly>
              <StatsEntry />
            </ProtectedRoute>
          }
        />
        <Route
          path="benchmarks"
          element={
            <ProtectedRoute adminOnly>
              <BenchmarkSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="billings"
          element={
            <ProtectedRoute adminOnly>
              <PartnerBillings />
            </ProtectedRoute>
          }
        />
        <Route
          path="financials"
          element={
            <ProtectedRoute>
              <Financials />
            </ProtectedRoute>
          }
        />
        <Route
          path="supervisor"
          element={
            <ProtectedRoute>
              <SupervisorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates"
          element={
            <ProtectedRoute>
              <FirmTemplates />
            </ProtectedRoute>
          }
        />
        <Route
          path="documents"
          element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          }
        />
        <Route
          path="feedback"
          element={
            <ProtectedRoute>
              <Feedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="shelf"
          element={
            <ProtectedRoute>
              <DigitalShelf />
            </ProtectedRoute>
          }
        />
        <Route
          path="shelf/board"
          element={
            <ProtectedRoute adminOnly>
              <ShelfBoard />
            </ProtectedRoute>
          }
        />
        <Route
          path="tax-calculator"
          element={
            <ProtectedRoute>
              <TaxCalculator />
            </ProtectedRoute>
          }
        />
        <Route
          path="training"
          element={
            <ProtectedRoute>
              <Training />
            </ProtectedRoute>
          }
        />
        <Route
          path="amortization"
          element={
            <ProtectedRoute>
              <Amortization />
            </ProtectedRoute>
          }
        />
        <Route
          path="passwords"
          element={
            <ProtectedRoute>
              <PasswordManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="contacts"
          element={
            <ProtectedRoute>
              <ContactDirectory />
            </ProtectedRoute>
          }
        />
        <Route
          path="handbook"
          element={
            <ProtectedRoute>
              <Handbook />
            </ProtectedRoute>
          }
        />
        <Route
          path="w9"
          element={
            <ProtectedRoute>
              <W9Requests />
            </ProtectedRoute>
          }
        />
      </Route>
      {/* Public vendor-facing W-9 form (no auth, no layout) */}
      <Route path="/w9-form/:token" element={<W9PublicForm />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
